import { useState, useCallback } from 'react';
import { adminAPI } from '@/services/api';

export interface CreditPlan {
  id: number;
  service_slug: string;
  plan_name: string;
  quota_type: string;
  quota_limit: number;
  monthly_base?: number;
  price_per_unit: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreditPlanFormData {
  plan_name: string;
  monthly_base: string;
  credit_per_call: string;
  billing_type: 'free' | 'usage' | 'flat' | 'base_plus_usage';
  is_active: boolean;
}

export const useCreditPlans = (serviceSlug: string) => {
  const [plans, setPlans] = useState<CreditPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const allPlans = await adminAPI.getServiceQuotaPlans(serviceSlug, true);
      // 모든 플랜을 표시 (quota_type이 'api_calls'인 플랜도 포함)
      // 실제로는 모든 ServiceQuotaPlan이 요금제 플랜이므로 필터링하지 않음
      setPlans(allPlans || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || '크레딧 플랜 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [serviceSlug]);

  const createPlan = useCallback(async (formData: CreditPlanFormData) => {
    const base = formData.monthly_base ? parseFloat(formData.monthly_base) : 0;
    const perCall = formData.credit_per_call ? parseFloat(formData.credit_per_call) : 0;
    
    if (isNaN(base) || base < 0 || isNaN(perCall) || perCall < 0) {
      throw new Error('월 기본 크레딧과 회당 크레딧은 0 이상의 숫자여야 합니다.');
    }

    // 플랜 유형별 제약 검증
    if (formData.billing_type === 'free') {
      if (base !== 0 || perCall !== 0) {
        throw new Error('완전 무료 플랜은 월 기본 크레딧과 회당 크레딧이 모두 0이어야 합니다.');
      }
    } else if (formData.billing_type === 'usage') {
      if (base !== 0 || perCall < 0.01) {
        throw new Error('사용량 플랜은 월 기본 크레딧 0, 회당 크레딧은 0.01 이상이어야 합니다.');
      }
    } else if (formData.billing_type === 'flat') {
      if (base < 0.01 || perCall !== 0) {
        throw new Error('월정액 플랜은 월 기본 크레딧 0.01 이상, 회당 크레딧 0이어야 합니다.');
      }
    } else if (formData.billing_type === 'base_plus_usage') {
      if (base < 0.01 || perCall < 0.01) {
        throw new Error('월 + 사용량 플랜은 월 기본/회당 크레딧이 모두 0.01 이상이어야 합니다.');
      }
    }

    await adminAPI.createQuotaPlan(serviceSlug, {
      plan_name: formData.plan_name,
      quota_type: 'api_calls',
      quota_limit: formData.billing_type === 'flat' || formData.billing_type === 'base_plus_usage' ? base : 0,
      monthly_base: base,
      price_per_unit: perCall,
      is_active: formData.is_active,
    });
    
    await fetchPlans();
  }, [serviceSlug, fetchPlans]);

  const updatePlan = useCallback(async (planId: number, formData: CreditPlanFormData) => {
    const base = formData.monthly_base ? parseFloat(formData.monthly_base) : 0;
    const perCall = formData.credit_per_call ? parseFloat(formData.credit_per_call) : 0;
    
    if (isNaN(base) || base < 0 || isNaN(perCall) || perCall < 0) {
      throw new Error('월 기본 크레딧과 회당 크레딧은 0 이상의 숫자여야 합니다.');
    }

    // 플랜 유형별 제약 검증
    if (formData.billing_type === 'free') {
      if (base !== 0 || perCall !== 0) {
        throw new Error('완전 무료 플랜은 월 기본 크레딧과 회당 크레딧이 모두 0이어야 합니다.');
      }
    } else if (formData.billing_type === 'usage') {
      if (base !== 0 || perCall < 0.01) {
        throw new Error('사용량 플랜은 월 기본 크레딧 0, 회당 크레딧은 0.01 이상이어야 합니다.');
      }
    } else if (formData.billing_type === 'flat') {
      if (base < 0.01 || perCall !== 0) {
        throw new Error('월정액 플랜은 월 기본 크레딧 0.01 이상, 회당 크레딧 0이어야 합니다.');
      }
    } else if (formData.billing_type === 'base_plus_usage') {
      if (base < 0.01 || perCall < 0.01) {
        throw new Error('월 + 사용량 플랜은 월 기본/회당 크레딧이 모두 0.01 이상이어야 합니다.');
      }
    }

    await adminAPI.updateQuotaPlan(serviceSlug, planId, {
      plan_name: formData.plan_name,
      quota_type: 'api_calls',
      quota_limit: formData.billing_type === 'flat' || formData.billing_type === 'base_plus_usage' ? base : 0,
      monthly_base: base,
      price_per_unit: perCall,
      is_active: formData.is_active,
    });
    
    await fetchPlans();
  }, [serviceSlug, fetchPlans]);

  const deletePlan = useCallback(async (planId: number) => {
    await adminAPI.deleteQuotaPlan(serviceSlug, planId);
    await fetchPlans();
  }, [serviceSlug, fetchPlans]);

  return {
    plans,
    loading,
    error,
    setError,
    fetchPlans,
    createPlan,
    updatePlan,
    deletePlan,
  };
};

